import React, { useState, useEffect } from 'react';
import { getDatabase, ref, onValue, get } from '../../Config/matuRtdb';
import { readPredictionSnapshot, watchPredictionSnapshot } from '../../Config/matudb';
import { 
  Users, 
  Crown, 
  Target, 
  TrendingUp, 
  DollarSign, 
  Calendar,
  Activity,
  Eye,
  Plus,
  UserCheck,
  UserX,
  Clock,
  CheckCircle,
  XCircle,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Star,
  Zap,
  Award,
  TrendingDown,
  Percent,
  BarChart,
  PieChart
} from 'lucide-react';

const DashboardOverview = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    vipUsers: 0,
    freeUsers: 0,
    totalPredictions: 0,
    freePredictions: 0,
    vipPredictions: 0,
    pendingPredictions: 0,
    wonPredictions: 0,
    lostPredictions: 0,
    successRate: 0,
    freeSuccessRate: 0,
    vipSuccessRate: 0,
    todayUsers: 0,
    todayPredictions: 0,
    todayVipConversions: 0,
    weeklyStats: {
      users: 0,
      predictions: 0,
      vipPredictions: 0,
      freePredictions: 0,
      successRate: 0
    },
    monthlyStats: {
      users: 0,
      predictions: 0,
      vipPredictions: 0,
      freePredictions: 0,
      successRate: 0
    },
    topPerformers: [],
    recentTrends: []
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('today');
  const [selectedMetric, setSelectedMetric] = useState('overview');

  useEffect(() => {
    loadDashboardData();
  }, [timeRange]);

  const loadDashboardData = async () => {
    const db = getDatabase();
    
    try {
      // Cargar usuarios
      const usersRef = ref(db, 'usuarios');
      const usersUnsubscribe = onValue(usersRef, (snapshot) => {
        if (snapshot.exists()) {
          const users = snapshot.val();
          const userIds = Object.keys(users);
          
          let vipCount = 0;
          let freeCount = 0;
          let todayUsers = 0;
          let weeklyUsers = 0;
          let monthlyUsers = 0;
          
          userIds.forEach(uid => {
            const user = users[uid];
            if (user.tipoCuenta === 'VIP' || user.isVip === true || user.vip === true) {
              vipCount++;
            } else {
              freeCount++;
            }
            
            // Usuarios por período
            if (user.createdAt) {
              const userDate = new Date(user.createdAt);
              const today = new Date();
              const diffTime = Math.abs(today - userDate);
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              
              if (diffDays <= 1) todayUsers++;
              if (diffDays <= 7) weeklyUsers++;
              if (diffDays <= 30) monthlyUsers++;
            }
          });
          
          setStats(prev => ({
            ...prev,
            totalUsers: userIds.length,
            vipUsers: vipCount,
            freeUsers: freeCount,
            todayUsers,
            weeklyStats: { ...prev.weeklyStats, users: weeklyUsers },
            monthlyStats: { ...prev.monthlyStats, users: monthlyUsers }
          }));
        }
      });

      // Cargar pronósticos gratis (abet)
      const freePredictionsUnsubscribe = watchPredictionSnapshot('abet', (snapshot) => {
        if (snapshot.exists()) {
          const predictions = snapshot.val();
          const predictionIds = Object.keys(predictions);
          
          let pending = 0;
          let won = 0;
          let lost = 0;
          let todayPredictions = 0;
          let weeklyPredictions = 0;
          let monthlyPredictions = 0;
          
          predictionIds.forEach(id => {
            const prediction = predictions[id];
            const state = prediction.state || 'pendiente';
            
            switch (state) {
              case 'pendiente':
                pending++;
                break;
              case 'ganado':
                won++;
                break;
              case 'perdido':
                lost++;
                break;
            }
            
            // Pronósticos por período
            if (prediction.createdAt) {
              const predDate = new Date(prediction.createdAt);
              const today = new Date();
              const diffTime = Math.abs(today - predDate);
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              
              if (diffDays <= 1) todayPredictions++;
              if (diffDays <= 7) weeklyPredictions++;
              if (diffDays <= 30) monthlyPredictions++;
            }
          });
          
          const total = pending + won + lost;
          const successRate = total > 0 ? Math.round((won / total) * 100) : 0;
          
          setStats(prev => ({
            ...prev,
            freePredictions: total,
            pendingPredictions: prev.pendingPredictions + pending,
            wonPredictions: prev.wonPredictions + won,
            lostPredictions: prev.lostPredictions + lost,
            freeSuccessRate: successRate,
            todayPredictions: prev.todayPredictions + todayPredictions,
            weeklyStats: { 
              ...prev.weeklyStats, 
              freePredictions: weeklyPredictions,
              predictions: prev.weeklyStats.predictions + weeklyPredictions
            },
            monthlyStats: { 
              ...prev.monthlyStats, 
              freePredictions: monthlyPredictions,
              predictions: prev.monthlyStats.predictions + monthlyPredictions
            }
          }));
        }
      });

      // Cargar pronósticos VIP (abetvip)
      const vipPredictionsUnsubscribe = watchPredictionSnapshot('abetvip', (snapshot) => {
        if (snapshot.exists()) {
          const predictions = snapshot.val();
          const predictionIds = Object.keys(predictions);
          
          let pending = 0;
          let won = 0;
          let lost = 0;
          let todayPredictions = 0;
          let weeklyPredictions = 0;
          let monthlyPredictions = 0;
          
          predictionIds.forEach(id => {
            const prediction = predictions[id];
            const state = prediction.state || 'pendiente';
            
            switch (state) {
              case 'pendiente':
                pending++;
                break;
              case 'ganado':
                won++;
                break;
              case 'perdido':
                lost++;
                break;
            }
            
            // Pronósticos por período
            if (prediction.createdAt) {
              const predDate = new Date(prediction.createdAt);
              const today = new Date();
              const diffTime = Math.abs(today - predDate);
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              
              if (diffDays <= 1) todayPredictions++;
              if (diffDays <= 7) weeklyPredictions++;
              if (diffDays <= 30) monthlyPredictions++;
            }
          });
          
          const total = pending + won + lost;
          const successRate = total > 0 ? Math.round((won / total) * 100) : 0;
          
          setStats(prev => {
            const totalPredictions = prev.freePredictions + total;
            const totalWon = prev.wonPredictions + won;
            const totalLost = prev.lostPredictions + lost;
            const overallSuccessRate = totalPredictions > 0 ? Math.round((totalWon / totalPredictions) * 100) : 0;
            
            return {
              ...prev,
              vipPredictions: total,
              totalPredictions,
              pendingPredictions: prev.pendingPredictions + pending,
              wonPredictions: totalWon,
              lostPredictions: totalLost,
              successRate: overallSuccessRate,
              vipSuccessRate: successRate,
              todayPredictions: prev.todayPredictions + todayPredictions,
              weeklyStats: { 
                ...prev.weeklyStats, 
                vipPredictions: weeklyPredictions,
                predictions: prev.weeklyStats.predictions + weeklyPredictions,
                successRate: prev.weeklyStats.predictions + weeklyPredictions > 0 ? 
                  Math.round(((prev.weeklyStats.won || 0) + won) / (prev.weeklyStats.predictions + weeklyPredictions) * 100) : 0
              },
              monthlyStats: { 
                ...prev.monthlyStats, 
                vipPredictions: monthlyPredictions,
                predictions: prev.monthlyStats.predictions + monthlyPredictions,
                successRate: prev.monthlyStats.predictions + monthlyPredictions > 0 ? 
                  Math.round(((prev.monthlyStats.won || 0) + won) / (prev.monthlyStats.predictions + monthlyPredictions) * 100) : 0
              }
            };
          });
        }
      });

      // Cargar actividad reciente y métricas avanzadas
      loadRecentActivity();
      loadAdvancedMetrics();

      setLoading(false);

      return () => {
        usersUnsubscribe();
        freePredictionsUnsubscribe();
        vipPredictionsUnsubscribe();
      };
    } catch (error) {
      console.error('Error cargando datos del dashboard:', error);
      setLoading(false);
    }
  };

  const loadRecentActivity = async () => {
    const db = getDatabase();
    const activities = [];
    
    try {
      // Obtener últimos usuarios
      const usersRef = ref(db, 'usuarios');
      const usersSnapshot = await get(usersRef);
      if (usersSnapshot.exists()) {
        const users = usersSnapshot.val();
        const sortedUsers = Object.entries(users)
          .sort(([,a], [,b]) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
          .slice(0, 5);
        
        sortedUsers.forEach(([uid, user]) => {
          activities.push({
            id: uid,
            type: 'user',
            title: `Nuevo usuario: ${user.name || user.email || 'Usuario'}`,
            description: user.tipoCuenta === 'VIP' ? 'Cuenta VIP' : 'Cuenta Gratis',
            timestamp: user.createdAt,
            icon: user.tipoCuenta === 'VIP' ? Crown : Users,
            color: user.tipoCuenta === 'VIP' ? 'text-yellow-400' : 'text-blue-400'
          });
        });
      }

      // Obtener últimos pronósticos gratis
      const freePredictionsSnapshot = await readPredictionSnapshot('abet');
      if (freePredictionsSnapshot.exists()) {
        const predictions = freePredictionsSnapshot.val();
        const sortedPredictions = Object.entries(predictions)
          .sort(([,a], [,b]) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
          .slice(0, 3);
        
        sortedPredictions.forEach(([id, prediction]) => {
          activities.push({
            id,
            type: 'prediction',
            title: `${prediction.homeTeam?.name || 'Equipo'} vs ${prediction.awayTeam?.name || 'Equipo'}`,
            description: `Pronóstico GRATIS: ${prediction.prediction} (${prediction.confidence}%)`,
            timestamp: prediction.createdAt,
            icon: Target,
            color: 'text-green-400',
            status: prediction.state || 'pendiente',
            category: 'free'
          });
        });
      }

      // Obtener últimos pronósticos VIP
      const vipPredictionsSnapshot = await readPredictionSnapshot('abetvip');
      if (vipPredictionsSnapshot.exists()) {
        const predictions = vipPredictionsSnapshot.val();
        const sortedPredictions = Object.entries(predictions)
          .sort(([,a], [,b]) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
          .slice(0, 3);
        
        sortedPredictions.forEach(([id, prediction]) => {
          activities.push({
            id,
            type: 'prediction',
            title: `${prediction.homeTeam?.name || 'Equipo'} vs ${prediction.awayTeam?.name || 'Equipo'}`,
            description: `Pronóstico VIP: ${prediction.prediction} (${prediction.confidence}%)`,
            timestamp: prediction.createdAt,
            icon: Crown,
            color: 'text-yellow-400',
            status: prediction.state || 'pendiente',
            category: 'vip'
          });
        });
      }

      // Ordenar por timestamp y tomar los 8 más recientes
      const sortedActivities = activities
        .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0))
        .slice(0, 8);

      setRecentActivity(sortedActivities);
    } catch (error) {
      console.error('Error cargando actividad reciente:', error);
    }
  };

  const loadAdvancedMetrics = async () => {
    const db = getDatabase();
    
    try {
      // Calcular tendencias y métricas avanzadas
      const trends = [];
      
      // Comparar tasas de éxito
      if (stats.freeSuccessRate > stats.vipSuccessRate) {
        trends.push({
          type: 'success',
          message: 'Los pronósticos gratis tienen mejor rendimiento',
          value: `${stats.freeSuccessRate}% vs ${stats.vipSuccessRate}%`
        });
      } else if (stats.vipSuccessRate > stats.freeSuccessRate) {
        trends.push({
          type: 'success',
          message: 'Los pronósticos VIP tienen mejor rendimiento',
          value: `${stats.vipSuccessRate}% vs ${stats.freeSuccessRate}%`
        });
      }

      // Analizar distribución de pronósticos
      const totalPreds = stats.freePredictions + stats.vipPredictions;
      if (totalPreds > 0) {
        const vipPercentage = Math.round((stats.vipPredictions / totalPreds) * 100);
        const freePercentage = Math.round((stats.freePredictions / totalPreds) * 100);
        
        trends.push({
          type: 'info',
          message: 'Distribución de pronósticos',
          value: `${freePercentage}% gratis, ${vipPercentage}% VIP`
        });
      }

      setStats(prev => ({
        ...prev,
        recentTrends: trends
      }));

    } catch (error) {
      console.error('Error cargando métricas avanzadas:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ganado': return 'text-green-400';
      case 'perdido': return 'text-red-400';
      case 'pendiente': return 'text-yellow-400';
      default: return 'text-gray-400';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Hace unos minutos';
    if (diffHours < 24) return `Hace ${diffHours} horas`;
    return date.toLocaleDateString('es-ES');
  };

  const getMetricData = () => {
    switch (timeRange) {
      case 'week':
        return stats.weeklyStats;
      case 'month':
        return stats.monthlyStats;
      default:
        return {
          users: stats.todayUsers,
          predictions: stats.todayPredictions,
          vipPredictions: 0,
          freePredictions: 0,
          successRate: stats.successRate
        };
    }
  };

  const metricData = getMetricData();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con filtros */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-white">Dashboard General</h2>
          <p className="text-white/60">Vista general de la aplicación</p>
        </div>
        <div className="flex space-x-2">
          {['today', 'week', 'month'].map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                timeRange === range
                  ? 'bg-blue-600 text-white'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              {range === 'today' ? 'Hoy' : range === 'week' ? 'Semana' : 'Mes'}
            </button>
          ))}
        </div>
      </div>

      {/* Estadísticas principales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {/* Total Usuarios */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-4 lg:p-6 border border-white/20">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-white/60 text-sm">Total Usuarios</p>
              <p className="text-2xl lg:text-3xl font-bold text-white">{stats.totalUsers}</p>
              <div className="flex items-center mt-2">
                <ArrowUpRight className="text-green-400 w-4 h-4 mr-1" />
                <span className="text-green-400 text-sm">+{metricData.users} {timeRange === 'today' ? 'hoy' : timeRange === 'week' ? 'esta semana' : 'este mes'}</span>
              </div>
            </div>
            <div className="bg-blue-500/20 p-3 rounded-lg">
              <Users className="text-blue-400 w-6 h-6" />
            </div>
          </div>
        </div>

        {/* Usuarios VIP */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-4 lg:p-6 border border-white/20">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-white/60 text-sm">Usuarios VIP</p>
              <p className="text-2xl lg:text-3xl font-bold text-yellow-400">{stats.vipUsers}</p>
              <div className="flex items-center mt-2">
                <span className="text-white/60 text-sm">
                  {stats.totalUsers > 0 ? Math.round((stats.vipUsers / stats.totalUsers) * 100) : 0}% del total
                </span>
              </div>
            </div>
            <div className="bg-yellow-500/20 p-3 rounded-lg">
              <Crown className="text-yellow-400 w-6 h-6" />
            </div>
          </div>
        </div>

        {/* Total Pronósticos */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-4 lg:p-6 border border-white/20">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-white/60 text-sm">Total Pronósticos</p>
              <p className="text-2xl lg:text-3xl font-bold text-white">{stats.totalPredictions}</p>
              <div className="flex items-center mt-2">
                <ArrowUpRight className="text-green-400 w-4 h-4 mr-1" />
                <span className="text-green-400 text-sm">+{metricData.predictions} {timeRange === 'today' ? 'hoy' : timeRange === 'week' ? 'esta semana' : 'este mes'}</span>
              </div>
            </div>
            <div className="bg-green-500/20 p-3 rounded-lg">
              <Target className="text-green-400 w-6 h-6" />
            </div>
          </div>
        </div>

        {/* Tasa de Éxito */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-4 lg:p-6 border border-white/20">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-white/60 text-sm">Tasa de Éxito</p>
              <p className="text-2xl lg:text-3xl font-bold text-green-400">{stats.successRate}%</p>
              <div className="flex items-center mt-2">
                <span className="text-white/60 text-sm">
                  {stats.wonPredictions} ganados / {stats.lostPredictions} perdidos
                </span>
              </div>
            </div>
            <div className="bg-green-500/20 p-3 rounded-lg">
              <TrendingUp className="text-green-400 w-6 h-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Métricas de Pronósticos por Tipo */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pronósticos Gratis */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 border border-white/20">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <Target className="w-5 h-5 mr-2 text-green-400" />
              Pronósticos Gratis
            </h3>
            <span className="text-green-400 font-bold text-xl">{stats.freePredictions}</span>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-white/60">Tasa de éxito</span>
              <span className="text-green-400 font-semibold">{stats.freeSuccessRate}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white/60">Esta semana</span>
              <span className="text-white font-semibold">{stats.weeklyStats.freePredictions}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white/60">Este mes</span>
              <span className="text-white font-semibold">{stats.monthlyStats.freePredictions}</span>
            </div>
          </div>
        </div>

        {/* Pronósticos VIP */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 border border-white/20">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <Crown className="w-5 h-5 mr-2 text-yellow-400" />
              Pronósticos VIP
            </h3>
            <span className="text-yellow-400 font-bold text-xl">{stats.vipPredictions}</span>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-white/60">Tasa de éxito</span>
              <span className="text-yellow-400 font-semibold">{stats.vipSuccessRate}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white/60">Esta semana</span>
              <span className="text-white font-semibold">{stats.weeklyStats.vipPredictions}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white/60">Este mes</span>
              <span className="text-white font-semibold">{stats.monthlyStats.vipPredictions}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Estadísticas detalladas */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Estado de Pronósticos */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            <BarChart className="w-5 h-5 mr-2" />
            Estado de Pronósticos
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-yellow-400 rounded-full mr-3"></div>
                <span className="text-white">Pendientes</span>
              </div>
              <span className="text-white font-semibold">{stats.pendingPredictions}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-400 rounded-full mr-3"></div>
                <span className="text-white">Ganados</span>
              </div>
              <span className="text-green-400 font-semibold">{stats.wonPredictions}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-red-400 rounded-full mr-3"></div>
                <span className="text-white">Perdidos</span>
              </div>
              <span className="text-red-400 font-semibold">{stats.lostPredictions}</span>
            </div>
          </div>
        </div>

        {/* Distribución de Usuarios */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            <PieChart className="w-5 h-5 mr-2" />
            Distribución de Usuarios
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Crown className="w-4 h-4 text-yellow-400 mr-3" />
                <span className="text-white">VIP</span>
              </div>
              <span className="text-yellow-400 font-semibold">{stats.vipUsers}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <UserCheck className="w-4 h-4 text-blue-400 mr-3" />
                <span className="text-white">Gratis</span>
              </div>
              <span className="text-blue-400 font-semibold">{stats.freeUsers}</span>
            </div>
            <div className="mt-4 pt-4 border-t border-white/20">
              <div className="flex items-center justify-between text-sm">
                <span className="text-white/60">Tasa de conversión VIP</span>
                <span className="text-white font-semibold">
                  {stats.totalUsers > 0 ? Math.round((stats.vipUsers / stats.totalUsers) * 100) : 0}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Métricas por Período */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            <TrendingUp className="w-5 h-5 mr-2" />
            Métricas por Período
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Calendar className="w-4 h-4 text-blue-400 mr-3" />
                <span className="text-white">Esta semana</span>
              </div>
              <span className="text-blue-400 font-semibold">{stats.weeklyStats.predictions}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Calendar className="w-4 h-4 text-purple-400 mr-3" />
                <span className="text-white">Este mes</span>
              </div>
              <span className="text-purple-400 font-semibold">{stats.monthlyStats.predictions}</span>
            </div>
            <div className="mt-4 pt-4 border-t border-white/20">
              <div className="flex items-center justify-between text-sm">
                <span className="text-white/60">Éxito semanal</span>
                <span className="text-white font-semibold">
                  {stats.weeklyStats.successRate}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Actividad Reciente */}
      <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 border border-white/20">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
          <Clock className="w-5 h-5 mr-2" />
          Actividad Reciente
        </h3>
        <div className="space-y-3">
          {recentActivity.length === 0 ? (
            <div className="text-center py-8">
              <Activity className="text-white/40 mx-auto mb-4 w-12 h-12" />
              <p className="text-white/60">No hay actividad reciente</p>
            </div>
          ) : (
            recentActivity.map((activity) => {
              const Icon = activity.icon;
              return (
                <div key={activity.id} className="flex items-center space-x-4 p-3 bg-white/5 rounded-lg">
                  <div className={`p-2 rounded-lg ${activity.color.replace('text-', 'bg-')}/20`}>
                    <Icon className={`w-4 h-4 ${activity.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{activity.title}</p>
                    <p className="text-white/60 text-sm">{activity.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white/60 text-xs">{formatDate(activity.timestamp)}</p>
                    {activity.status && (
                      <span className={`text-xs ${getStatusColor(activity.status)}`}>
                        {activity.status}
                      </span>
                    )}
                    {activity.category && (
                      <span className={`text-xs ${activity.category === 'vip' ? 'text-yellow-400' : 'text-green-400'}`}>
                        {activity.category === 'vip' ? 'VIP' : 'GRATIS'}
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview; 